import { MarketHoursEnum } from '@api/api/market-overview';
import { parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { TFunction } from 'i18next';

export const convertESTTimeToUserTimezone = (estTimeString: string): string => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const today = new Date();
  const estTime = parse(estTimeString, 'h:mm a', today);
  const estDate = fromZonedTime(estTime, 'America/New_York');

  const formattedTime = formatInTimeZone(estDate, userTimezone, 'h:mm a');
  const timeZoneName =
    new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      timeZoneName: 'short',
    })
      .formatToParts(estDate)
      .find(part => part.type === 'timeZoneName')?.value ?? '';

  return `${formattedTime} ${timeZoneName}`;
};

export const getMarketInsightTitle = (
  marketHours: MarketHoursEnum,
  t: TFunction
) => {
  const convertedTime = getMarketInsightTime(marketHours);

  switch (marketHours) {
    case 'pre_market': {
      return {
        card: `${convertedTime} | ${t('whatsNew:preMarket.cardTitle')}`,
        legacyCardTitle: t('whatsNew:preMarket.legacyCardTitle'),
        title: `${convertedTime} | ${t('whatsNew:preMarket.title')}`,
      };
    }

    case 'open_market': {
      return {
        card: `${convertedTime} | ${t('whatsNew:midday.cardTitle')}`,
        legacyCardTitle: t('whatsNew:midday.legacyCardTitle'),
        title: `${convertedTime} | ${t('whatsNew:midday.title')}`,
      };
    }

    default: {
      return {
        card: `${convertedTime} | ${t('whatsNew:closedMarket.cardTitle')}`,
        legacyCardTitle: t('whatsNew:closedMarket.legacyCardTitle'),
        title: `${convertedTime} | ${t('whatsNew:closedMarket.title')}`,
      };
    }
  }
};

export const getMarketInsightTime = (marketHours: MarketHoursEnum): string => {
  switch (marketHours) {
    case 'pre_market': {
      const estTime = '9:00 AM';
      return convertESTTimeToUserTimezone(estTime);
    }

    case 'open_market': {
      const estTime = '2:00 PM';
      return convertESTTimeToUserTimezone(estTime);
    }

    default: {
      const estTime = '5:00 PM';
      return convertESTTimeToUserTimezone(estTime);
    }
  }
};

export const getMarketInsightIcon = (marketHours: MarketHoursEnum) => {
  switch (marketHours) {
    case 'pre_market':
      return 'SunriseArrow';
    case 'open_market':
      return 'SunSolid';
    default:
      return 'SunsetArrow';
  }
};
